import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Loader2 } from "lucide-react";

// Color palette for players (color-blind friendly)
const PLAYER_COLORS = [
  "#C6A87C", // Gold
  "#4DFFFF", // Cyan
  "#FF6B6B", // Red
  "#98D8C8", // Mint
  "#F7DC6F", // Yellow
  "#BB8FCE", // Purple
  "#85C1E9", // Light Blue
  "#F8B500", // Orange
];

export default function LeaguePerformanceGraph({ chartData, players, loading }) {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="text-center py-8 text-[#64748B]">
        Henüz grafik verisi yok. En az 1 hafta sonucu gerekli.
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#0A1635] border border-white/10 rounded-lg p-3 shadow-xl">
          <p className="text-[#C6A87C] font-bold mb-2">{label}</p>
          {payload
            .sort((a, b) => b.value - a.value)
            .map((entry, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-white">{entry.name}:</span>
                <span className="text-[#4DFFFF] font-bold">{entry.value} puan</span>
              </div>
            ))}
        </div>
      );
    }
    return null;
  };

  const handlePlayerClick = (username) => {
    setSelectedPlayer(selectedPlayer === username ? null : username);
  };

  return (
    <div className="glass-card p-4 sm:p-6" data-testid="performance-graph">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C]">
          Performans Grafiği
        </h3>
        
        {/* Player filter buttons for mobile */}
        {isMobile && (
          <div className="flex flex-wrap gap-2">
            {players?.slice(0, 4).map((player, index) => (
              <button
                key={player.user_id}
                onClick={() => handlePlayerClick(player.username)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedPlayer === player.username
                    ? "ring-2 ring-white"
                    : "opacity-70 hover:opacity-100"
                }`}
                style={{
                  backgroundColor: `${PLAYER_COLORS[index % PLAYER_COLORS.length]}20`,
                  color: PLAYER_COLORS[index % PLAYER_COLORS.length],
                }}
              >
                {player.username}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={`${isMobile ? "overflow-x-auto" : ""}`}>
        <div className={`${isMobile ? "min-w-[500px]" : ""}`}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5F" />
              <XAxis
                dataKey="week"
                tick={{ fill: "#64748B", fontSize: 12 }}
                axisLine={{ stroke: "#1E3A5F" }}
              />
              <YAxis
                tick={{ fill: "#64748B", fontSize: 12 }}
                axisLine={{ stroke: "#1E3A5F" }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: "10px" }}
                onClick={(e) => handlePlayerClick(e.value)}
                formatter={(value) => (
                  <span
                    className={`cursor-pointer transition-opacity ${
                      selectedPlayer && selectedPlayer !== value
                        ? "opacity-30"
                        : "opacity-100"
                    }`}
                  >
                    {value}
                  </span>
                )}
              />
              {players?.map((player, index) => (
                <Line
                  key={player.user_id}
                  type="monotone"
                  dataKey={player.username}
                  name={player.name || player.username}
                  stroke={PLAYER_COLORS[index % PLAYER_COLORS.length]}
                  strokeWidth={selectedPlayer === player.username ? 4 : 2}
                  dot={{
                    fill: PLAYER_COLORS[index % PLAYER_COLORS.length],
                    strokeWidth: 0,
                    r: selectedPlayer === player.username ? 6 : 4,
                  }}
                  activeDot={{ r: 8 }}
                  opacity={
                    selectedPlayer && selectedPlayer !== player.username
                      ? 0.2
                      : 1
                  }
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick stats below graph */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/10">
        {players?.slice(0, 4).map((player, index) => {
          const trend = player.trend;
          const trendIcon = trend === "rising" ? "↑" : trend === "falling" ? "↓" : "→";
          const trendColor = trend === "rising" ? "text-green-400" : trend === "falling" ? "text-red-400" : "text-[#64748B]";
          
          return (
            <div
              key={player.user_id}
              className="text-center cursor-pointer hover:bg-white/5 rounded-lg p-2 transition-colors"
              onClick={() => handlePlayerClick(player.username)}
            >
              <div className="flex items-center justify-center gap-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: PLAYER_COLORS[index % PLAYER_COLORS.length] }}
                />
                <span className="text-xs text-[#64748B]">{player.username}</span>
                <span className={`text-xs ${trendColor}`}>{trendIcon}</span>
              </div>
              <div className="text-sm font-bold text-white">{player.total_points} puan</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
